import { useEffect, useMemo, useRef, useState } from "react";
import posthog from "posthog-js";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../services/Firebase";
import { ProposalBrandAsset, rankProposals } from "../../lib/postProposals";
import type { PendingTopicData } from "../autopilot/TopicCard";
import { FirstPostIntro } from "./FirstPostIntro";
import { IdeaMoodBoard } from "../autopilot/IdeaMoodBoard";
import { Button } from "../ui/button";
import { SetupNotice } from "./OnboardingChrome";

/**
 * Business setup ends by designing one post from the ideas just found. The
 * post is made in the workspace, next to more ideas like it.
 */
export function FirstPostStep({
  kitId,
  connectionIds,
  since,
  onStarted,
  onSkip,
  busy,
}: {
  kitId: string;
  connectionIds: string[];
  /** When this onboarding began; only posts started after it resume the hand-off. */
  since?: number;
  /** Resolves false when the workspace could not be opened, so it can be retried. */
  onStarted: (postId: string) => Promise<boolean>;
  onSkip: () => void;
  busy: boolean;
}) {
  const { currentUser, activeWorkspaceId, customerData } = useAuth();
  const [topics, setTopics] = useState<PendingTopicData[] | null>(null);
  const [assets, setAssets] = useState<ProposalBrandAsset[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  // Set once the first post exists, so a failed hand-off retries opening the
  // workspace instead of drafting (and charging for) another post.
  const [startedPostId, setStartedPostId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const viewed = useRef(false);
  const resumed = useRef(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    return onSnapshot(
      query(
        collection(db, "pending_topics"),
        where("userId", "==", activeWorkspaceId),
      ),
      (snapshot) =>
        setTopics(
          snapshot.docs.map(
            (item) => ({ id: item.id, ...item.data() }) as PendingTopicData,
          ),
        ),
      () => setTopics([]),
    );
  }, [activeWorkspaceId]);
  useEffect(
    () =>
      onSnapshot(
        doc(db, "brand_kits", kitId),
        (snapshot) => setAssets(snapshot.data()?.brandAssets || []),
        () => setAssets([]),
      ),
    [kitId],
  );

  const proposals = useMemo(
    () => (topics ? rankProposals(topics, assets) : []),
    [topics, assets],
  );
  // Ideas keep arriving during the scan. Stay on the proposal being viewed.
  const index = Math.max(
    0,
    proposals.findIndex((proposal) => proposal.topic.id === currentId),
  );
  const current = proposals[index];

  // If the first post was already started (e.g. setup could not be marked
  // complete), finish the hand-off instead of offering a second choice.
  useEffect(() => {
    if (resumed.current || !topics) return;
    const started = topics.find(
      (topic) =>
        topic.status === "generated" &&
        topic.postId &&
        (!since || (topic.generatedAt ?? 0) >= since),
    );
    if (started?.postId) {
      resumed.current = true;
      void handOff(started.postId);
    }
    // handOff is stable in behavior; re-running on its identity is not needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics, since]);

  useEffect(() => {
    if (viewed.current || !current) return;
    viewed.current = true;
    // Keep this idea on screen while more ideas arrive and rankings shift.
    setCurrentId(current.topic.id);
    posthog.capture("onboarding_first_post_viewed", {
      proposal_count: proposals.length,
      own_photo_slides: current.ownPhotoCount,
      slide_count: current.slides.length,
    });
  }, [current, proposals.length]);

  const handOff = async (postId: string) => {
    setStartedPostId(postId);
    setStarting(true);
    const opened = await onStarted(postId);
    if (!opened) setStarting(false);
  };

  const design = async () => {
    if (!current || starting || !currentUser || !activeWorkspaceId) return;
    if (startedPostId) {
      await handOff(startedPostId);
      return;
    }
    setStarting(true);
    setError("");
    // The topic snapshot can report the new post before this request returns;
    // this request owns the hand-off.
    resumed.current = true;
    posthog.capture("generation_started", {
      entry_point: "onboarding_proposal",
      source_count: 1,
      source_types: ["url"],
      mode: "single",
    });
    posthog.capture("onboarding_first_post_started", {
      proposal_index: index,
      proposal_count: proposals.length,
      own_photo_slides: current.ownPhotoCount,
      slide_count: current.slides.length,
    });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/autopilot/topics/${current.topic.id}/generate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${await currentUser.getIdToken()}`,
            "Content-Type": "application/json",
            "x-workspace-id": activeWorkspaceId,
          },
          // Explicit settings are remembered for this source, so ideas in the
          // workspace can be designed in one click with the same choices.
          body: JSON.stringify({
            settings: {
              brandKitId: kitId,
              connectionIds,
              templateIds: [],
              visualStyleId: "auto",
            },
          }),
        },
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.postId)
        throw new Error(result.error || "We couldn’t start this post. Try again, or skip for now.");
      await handOff(result.postId);
    } catch (err) {
      resumed.current = false;
      setError((err as Error).message);
      setStarting(false);
    }
  };

  if (!topics || !current) {
    return (
      <div role="status" className="flex flex-col items-center gap-6 py-10">
        <Loader2
          aria-hidden="true"
          className="h-6 w-6 animate-spin text-brand motion-reduce:animate-none"
        />
        <span className="sr-only">Gathering ideas for your first post…</span>
        {topics && (
          <Button variant="outline" className="h-10 rounded-md px-5" disabled={busy} onClick={onSkip}>
            Go to my workspace
          </Button>
        )}
      </div>
    );
  }

  const credits = customerData
    ? (customerData.videoCredits ?? 0) + (customerData.bonusCredits ?? 0)
    : undefined;
  return (
    <div className="mx-auto w-full max-w-3xl">
      <FirstPostIntro />
      <IdeaMoodBoard
        key={current.topic.id}
        topic={current.topic}
        slides={current.slides}
      />
      {error && (
        <SetupNotice className="mx-auto mt-5">{error}</SetupNotice>
      )}
      <div className="mt-8 flex flex-col items-center gap-3">
        <Button
          variant="brand"
          size="lg"
          className="h-12 w-full rounded-md px-7 font-semibold sm:w-auto"
          disabled={starting || busy}
          onClick={() => void design()}
        >
          {starting && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
          )}
          {startedPostId ? "Open my workspace" : "Create this post"}
          {!starting && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
        <p className="text-xs text-muted-foreground" hidden={!!startedPostId}>
          {typeof credits === "number"
            ? `Uses 1 of your ${credits} credits`
            : "Uses 1 credit"}
          {" · Review before publishing"}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-5">
          {proposals.length > 1 && (
            <Button
              variant="link"
              className="text-muted-foreground"
              disabled={starting}
              onClick={() =>
                setCurrentId(proposals[(index + 1) % proposals.length].topic.id)
              }
            >
              Show another idea
            </Button>
          )}
          <Button
            variant="link"
            className="text-muted-foreground"
            disabled={starting || busy}
            onClick={onSkip}
          >
            Skip for now
          </Button>
        </div>
      </div>
    </div>
  );
}
